import { useNavigate, Link, useLocation } from "react-router-dom";
import { ShieldCheck, Plus, LogOut, Heart, ShieldAlert, BookLock, Building2 } from "lucide-react";
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import AddSupervisorModal from "@/components/AddSupervisorModal";
import NotificationBell from "@/components/NotificationBell";
import { toast } from "sonner";

export default function Navbar() {
  const { user, logout, setAuthOpen } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [addOpen, setAddOpen] = useState(false);

  const handleAdd = () => {
    if (!user) {
      setAuthOpen(true);
      toast.info("Sign in to add a supervisor");
      return;
    }
    setAddOpen(true);
  };

  const [hasEmployer, setHasEmployer] = useState(false);
  useEffect(() => {
    if (!user) { setHasEmployer(false); return; }
    api.get("/employer/companies").then((r) => setHasEmployer(r.data.length > 0)).catch(() => setHasEmployer(false));
  }, [user]);

  const navItem = (to, label, testid, extra = "") => (
    <Link
      to={to}
      data-testid={testid || `nav-${label.toLowerCase()}-link`}
      className={`hidden md:inline-block whitespace-nowrap text-sm font-semibold tracking-wide transition-colors hover:text-emerald-700 ${extra} ${
        location.pathname === to ? "text-emerald-700" : "text-slate-600"
      }`}
    >
      {label}
    </Link>
  );

  return (
    <header className="sticky top-0 z-50 backdrop-blur-md bg-white/85 border-b border-slate-200/80">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between gap-4">
        <button
          data-testid="nav-brand-logo"
          onClick={() => navigate("/")}
          className="flex items-center gap-2 group"
        >
          <div className="h-9 w-9 rounded-lg bg-slate-900 flex items-center justify-center group-hover:bg-emerald-600 transition-colors">
            <ShieldCheck size={20} className="text-white" />
          </div>
          <span className="font-display font-800 text-2xl tracking-tight text-slate-900" style={{ fontWeight: 800 }}>
            my360<span className="text-emerald-600">boss</span>
          </span>
        </button>

        <nav className="flex items-center gap-6">
          {navItem("/supervisors", "Browse")}
          {navItem("/companies", "Companies")}
          {navItem("/compare", "Compare")}
          {navItem("/analytics", "Analytics")}
          {navItem("/faq", "FAQ")}
          {navItem("/insights", "Insights", "nav-insights-link", "!hidden lg:!inline-block")}
          {navItem("/pricing", "Pricing", "nav-pricing-link", "!hidden lg:!inline-block")}
          {navItem("/for-employers", "For Employers", "nav-employers-link", "!hidden lg:!inline-block")}

          <Button
            data-testid="nav-add-supervisor-btn"
            onClick={handleAdd}
            variant="outline"
            className="hidden sm:flex border-slate-300 hover:border-emerald-500 hover:text-emerald-700 gap-1.5 rounded-full"
          >
            <Plus size={16} /> Add Supervisor
          </Button>

          {user ? (
            <div className="flex items-center gap-2">
              <NotificationBell />
              {user.role === "admin" && (
                <Button
                  data-testid="nav-moderation-btn"
                  onClick={() => navigate("/admin")}
                  variant="ghost"
                  size="icon"
                  className="text-slate-500 hover:text-rose-600"
                  title="Moderation queue"
                >
                  <ShieldAlert size={18} />
                </Button>
              )}
              {hasEmployer && (
                <Button data-testid="nav-employer-btn" onClick={() => navigate("/employer")} variant="ghost" size="icon" className="text-emerald-700 hover:text-emerald-800" title="Employer Dashboard">
                  <Building2 size={18} />
                </Button>
              )}
              <Button
                data-testid="nav-vault-btn"
                onClick={() => navigate("/vault")}
                variant="ghost"
                size="icon"
                className="text-slate-500 hover:text-emerald-700"
                title="Documentation Vault"
              >
                <BookLock size={18} />
              </Button>
              <Button
                data-testid="nav-favorites-btn"
                onClick={() => navigate("/favorites")}
                variant="ghost"
                size="icon"
                className="text-slate-500 hover:text-rose-500"
                title="Saved"
              >
                <Heart size={18} />
              </Button>
              <span className="hidden xl:inline text-sm text-slate-500 font-mono ml-1">
                {user.name}
              </span>
              <Button
                data-testid="nav-logout-btn"
                onClick={logout}
                variant="ghost"
                size="icon"
                className="text-slate-500 hover:text-rose-600"
              >
                <LogOut size={18} />
              </Button>
            </div>
          ) : (
            <Button
              data-testid="nav-auth-btn"
              onClick={() => setAuthOpen(true)}
              className="bg-slate-900 hover:bg-emerald-600 text-white rounded-full px-5"
            >
              Sign In
            </Button>
          )}
        </nav>
      </div>
      <AddSupervisorModal open={addOpen} onOpenChange={setAddOpen} />
    </header>
  );
}
